import React, { useCallback, useRef, useState } from 'react';
import useCategoriesContext from '@sls-react/contexts/CategoriesContext';
import { useSession } from '@sls-react/contexts/SessionContext';
import selectEnv from '@sls-react/utils/selectEnv';
import ShopLink from '@sls-react/components/Links/ShopLink';
import ValueGuideLink from '@sls-react/components/Links/ValueGuideLink';
import HelpLink from '@sls-react/components/Links/HelpLink';
import { useDelayFn } from '@sls-react/hooks/useDelayFn';
import ChevronRight from '@sls-react/static/svg/ChevronRight';
import ScaleFromTopFade from '@sls-react/components/Transitions/ScaleFromTopFade';
import { RailsCategory } from '@sls-react/typings/services/rails/item';
import HorizontalDots from '@sls-react/components/Icons/HorizontalDots';

interface Props {}

const DesktopHeaderCategoryNavigationRow: React.FC<Props> = () => {
  const categories = useCategoriesContext();
  const { loggedIn } = useSession();
  const rowRef = useRef<HTMLUListElement>(null);

  const [hovered, setHovered] = useState<
    | null
    | { id: number; left?: number; right?: number }
    | { id: 'more'; left?: number; right?: number }
  >(null);
  const [hoverOpen, setHoverOpen] = useState(false);

  const [setHoverOpenTimeout, clearHoverOpenTimeout] = useDelayFn(
    (open: boolean) => setHoverOpen(open),
    150,
  );

  const gatherHiddenElements = useCallback(() => {
    const first = rowRef.current?.children.item(0) as HTMLLIElement | null;
    const elements: RailsCategory[] = [];

    if (rowRef.current && categories && first) {
      for (let i = 0; i < rowRef.current.children.length; i++) {
        const element = rowRef.current.children.item(i) as HTMLLIElement | null;

        if (element && element.offsetTop > first.offsetTop) {
          const categoryId = Number(element?.dataset.categoryId);

          if (categoryId) {
            const found = categories.find(
              category => category.id == categoryId,
            );
            if (found) {
              elements.push(found);
            }
          }
        }
      }
    }

    return elements;
  }, [categories, rowRef]);

  const hoveredCategory =
    hovered &&
    categories &&
    categories.find(category => category.id === hovered.id);

  const cx =
    'px-4 py-3 rounded hover:bg-green-500 hover:text-white transition-colors duration-200';

  return (
    <div className="relative flex items-center pb-1 pl-6 pr-8 mt-4">
      <div
        className="flex items-center"
        onMouseOut={() => {
          clearHoverOpenTimeout();
          setHoverOpenTimeout(false);
        }}
      >
        <ul
          className="flex flex-wrap justify-between h-6 overflow-hidden leading-snug"
          ref={rowRef}
        >
          {categories?.map(category => (
            <li key={category.id} data-category-id={category.id}>
              <ShopLink
                path={category.url}
                onMouseOver={e => {
                  if (category?.children && category?.children?.length! > 0) {
                    setHovered({
                      id: category.id,
                      left: e.currentTarget.offsetLeft,
                    });
                  } else {
                    setHovered({
                      id: -1,
                      left: -1000,
                    });
                  }
                  setHoverOpenTimeout(true);
                }}
                className="block px-2 py-1"
              >
                {category.name}
              </ShopLink>

              <ul className="hidden">
                {category.children?.map(child => (
                  <li key={child.id}>
                    <ShopLink path={child.url}>{child.name}</ShopLink>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        <div
          role="presentation"
          className="relative ml-2 cursor-pointer hover:text-blue-500 top-0.5"
          onMouseOver={e => {
            setHovered({
              id: 'more',
              left: e.currentTarget.offsetLeft - 8,
            });
            setHoverOpenTimeout(true);
          }}
          onMouseOut={() => setHoverOpenTimeout(false)}
        >
          <HorizontalDots className="w-6 h-6" />
        </div>
      </div>

      <div className="flex flex-shrink-0 ml-32 space-x-2">
        {!loggedIn && (
          <HelpLink className="font-semibold">Help Center</HelpLink>
        )}

        <ShopLink
          type="nav"
          path={selectEnv('f/fanshop/l163447', 'leagues/l199326')}
          className="font-semibold"
        >
          Fan Shop
        </ShopLink>

        <ValueGuideLink className="font-semibold">Value Guide</ValueGuideLink>
      </div>

      <ScaleFromTopFade
        as="ul"
        show={
          !!(
            hoverOpen &&
            ((hovered?.id !== 'more' && hoveredCategory?.children) ||
              hovered?.id === 'more')
          )
        }
        className="absolute z-20 w-64 p-2 overflow-auto text-xs bg-white border rounded shadow-lg top-8"
        style={{ left: hovered?.left, right: hovered?.right, maxHeight: 500 }}
        onMouseOver={() => {
          clearHoverOpenTimeout();
          setHoverOpen(true);
        }}
        onMouseOut={() => setHoverOpenTimeout(false)}
      >
        {hovered?.id === 'more' &&
          gatherHiddenElements().map(category => (
            <li key={category.id}>
              <ShopLink
                path={category.url}
                className={`flex items-center space-x-2 ${cx}`}
              >
                <span>{category.name}</span>
                <ChevronRight fill="currentColor" className="w-2 h-2" />
              </ShopLink>
            </li>
          ))}

        {hovered?.id !== 'more' && hoveredCategory?.children && (
          <>
            <li role="presentation" className="px-4 py-3 font-bold uppercase">
              Top Categories
            </li>

            {hoveredCategory?.children?.slice(0, 8).map(child => (
              <li key={child.id}>
                <ShopLink path={child.url} className={`block ${cx}`}>
                  {child.name}
                </ShopLink>
              </li>
            ))}

            {hoveredCategory && (
              <li>
                <ShopLink
                  path={hoveredCategory?.url}
                  className={`flex items-center space-x-2 ${cx}`}
                >
                  <span>All {hoveredCategory.name}</span>
                  <ChevronRight fill="currentColor" className="w-2 h-2" />
                </ShopLink>
              </li>
            )}
          </>
        )}
      </ScaleFromTopFade>
    </div>
  );
};

export default DesktopHeaderCategoryNavigationRow;
